import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createNCR } from "../../../services/api/qa/ncrService";
import { fetchProjectsApi } from "../../../services/api/boq/projectService";
import { fetchBoqByProject, fetchBoqById } from "../../../services/api/boq/boqService";
import toast from "react-hot-toast";

function CreateNCR() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [boqHeaders, setBoqHeaders] = useState([]);
    const [boqItems, setBoqItems] = useState([]);

    const [formData, setFormData] = useState({
        project_id: "",
        boq_header_id: "",
        boq_item_id: "",
        title: "",
        severity: "",
        due_date: "",
        issue_description: "",
        reported_by: 1,
        status: "created",
        corrective_action: "",
    });
    // Load projects on mount
    useEffect(() => {
        const loadProjects = async () => {
            try {
                const res = await fetchProjectsApi();
                setProjects(res?.data?.data?.data || []);
            } catch (err) {
                console.error("Failed to load projects", err);
            }
        };
        loadProjects();
    }, []);
    // Load BOQ headers whenever project changes
    useEffect(() => {
        const loadBoqHeaders = async () => {
            if (!formData.project_id) {
                setBoqHeaders([]);
                setBoqItems([]);
                return;
            }

            try {
                const res = await fetchBoqByProject(formData.project_id);

                const headers = res.map(item => ({
                    id: item.id,
                    name: item.boq_name
                }));

                const uniqueHeaders = Array.from(
                    new Map(headers.map(h => [h.id, h])).values()
                );

                setBoqHeaders(uniqueHeaders);
                setBoqItems([]);

                setFormData(prev => ({
                    ...prev,
                    boq_header_id: "",
                    boq_item_id: ""
                }));

            } catch (err) {
                console.error("Failed to load BOQ headers", err);
            }
        };

        loadBoqHeaders();
    }, [formData.project_id]);

    // Load BOQ items whenever boqheader changes
    useEffect(() => {
        const loadBoqItems = async () => {
            if (!formData.boq_header_id) {
                setBoqItems([]);

                setFormData(prev => ({
                    ...prev,
                    boq_item_id: ""
                }));

                return;
            }

            try {
                const items = await fetchBoqById(formData.boq_header_id);

                setBoqItems(items?.data?.items || []);

                setFormData(prev => ({
                    ...prev,
                    boq_item_id: ""
                }));

            } catch (err) {
                console.error("Failed to load BOQ items", err);
            }
        };

        loadBoqItems();
    }, [formData.boq_header_id]);

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Handle input change
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    // Validation
    const validate = () => {
        let newErrors = {};

        if (!formData.project_id) newErrors.project_id = "Project is required";
        if (!formData.boq_item_id) newErrors.boq_item_id = "BOQ Item is required";
        if (!formData.title.trim()) newErrors.title = "Title is required";
        if (!formData.severity) newErrors.severity = "Severity is required";
        if (!formData.due_date) newErrors.due_date = "Due date is required";
        if (!formData.issue_description.trim())
            newErrors.issue_description = "Issue description is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);
            formData.reported_by = 1; // Hardcoded for now, replace with actual user ID
            await createNCR(formData);
            toast.success("NCR Created Successfully");
            navigate("/ncr");
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto bg-white shadow p-6 rounded">
            <h2 className="text-2xl font-bold mb-6">Create NCR</h2>

            <form onSubmit={handleSubmit} className="space-y-4">

                {/* Project */}
                <select
                    name="project_id"
                    value={formData.project_id}
                    onChange={handleChange}
                >
                    <option value="">Select Project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                </select>

                {/* BOQ Header */}
                <select
                    name="boq_header_id"
                    value={formData.boq_header_id}
                    onChange={handleChange}
                    disabled={!formData.project_id}
                >
                    <option value="">Select BOQ Header</option>
                    {boqHeaders.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>

                {/* BOQ Item */}
                <select
                    name="boq_item_id"
                    value={formData.boq_item_id}
                    onChange={handleChange}
                    disabled={!formData.boq_header_id}
                >
                    <option value="">Select BOQ Item</option>
                    {boqItems.map(item => <option key={item.id} value={item.id}>{item.item_name}</option>)}
                </select>

                {/* Assign TO */}
                {/* <div>
                    <label>Assign To</label>
                    <select
                        name="assigned_to"
                        value={formData.assigned_to}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                    >
                        <option value="">Select User</option>
                        <option value="1">User A</option>
                        <option value="2">User B</option>
                    </select>
                </div> */}



                {/* Title */}
                <div>
                    <label>Title</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                    />
                    {errors.title && <p className="text-red-500 text-sm">{errors.title}</p>}
                </div>

                {/* Issue Description */}
                <div>
                    <label>Issue Description</label>
                    <textarea
                        name="issue_description"
                        value={formData.issue_description}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                        rows="4"
                    />
                    {errors.issue_description && (
                        <p className="text-red-500 text-sm">{errors.issue_description}</p>
                    )}
                </div>

                {/* Severity */}
                <div>
                    <label>Severity</label>
                    <select
                        name="severity"
                        value={formData.severity}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                    >
                        <option value="">Select Severity</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                    </select>
                    {errors.severity && <p className="text-red-500 text-sm">{errors.severity}</p>}
                </div>

                {/* Due Date */}
                <div>
                    <label>Due Date</label>
                    <input
                        type="date"
                        name="due_date"
                        value={formData.due_date}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                    />
                    {errors.due_date && <p className="text-red-500 text-sm">{errors.due_date}</p>}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        {loading ? "Creating..." : "Create NCR"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default CreateNCR;